import { Injectable } from '@angular/core';
import { IMeal, IMealPlanDetail, IMenuLibraryItem, IPlanDetail, Meal, MealPlan, User } from '../models';
import { HelperService } from './helper.service';
import { DatabaseService } from './database.service';
import { equalTo, limitToLast, orderByChild, ThenableReference } from '@angular/fire/database';
import { StorageService } from './storage.service';
import { LocalAttachment } from './camera.service';

@Injectable({
    providedIn: 'root'
})
  

export class MealPlanService {

  constructor(
    private dbService: DatabaseService,
    private helperService: HelperService,
    private storageService: StorageService,
  ) { }

  async getMealPlanAsPromise(userId: string, planId: string) {
    const snap = await this.dbService.getSnapshot(`/mealPlans/${userId}/${planId}`);
    return snap? new MealPlan({
      ...snap,
      id: planId
    }) : null;
  }

  async getMealPlanMostRecent(userId: string, planId: string) {
    const snap = await this.dbService.getSnapshot(`/mealPlans/${userId}/${planId}`, orderByChild("creationDate"), limitToLast(1));
    if(snap) {
      const items = this.helperService.transformFirebaseObjectToArray(snap.val());
      return new MealPlan(items[0]);
    } else {
      return undefined
    }
  }

  getMealPlanCommentsList(userId: string, planId: string) {
    return this.dbService.getListByRefTest(`/mealPlans/${userId}/${planId}/comments`);
  }

  AddMealPlansComment(userId, mealPlanId, comment) {
    return this.dbService.pushByRef(`/mealPlans/${userId}/${mealPlanId}/comments`, comment);
  }

  async addMealPlanCommentAttachment(
    userId,
    reportId,
    user: User,
    attachment: LocalAttachment,
    organization?: string
  ) {
    const storagePath = `users/${userId}/mealPlans/${reportId}/comments/${attachment.name}`;
    let attachmentRef = null;

    if(attachment.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file);
    }

    if(attachment.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }

    if(!attachmentRef) {
      throw new Error('No attachment reference found');
    }

    return this.AddMealPlansComment(userId, reportId, {
      comment: null,
      creationDate: new Date().toISOString(),
      createdBy: user.uid,
      createdByName: user.getFullName(),
      organization: organization || null,
      attachments: [{
        url: attachmentRef,
        fileName: attachment.name,
        type: attachment.type,
      }]
    });
  }  

  updateMealPlanComment(userId, mealPlanId, commentId: string, comment) {
    return this.dbService.updateRef(`/mealPlans/${userId}/${mealPlanId}/comments/${commentId}`, comment);
  }

  deleteMealPlanComment(userId: string, mealPlanId: string, commentId: string) {
    return this.dbService.removeRef(`/mealPlans/${userId}/${mealPlanId}/comments/${commentId}`);
  }

  deleteMealPlan(userId: string, programId: string): Promise<any> {
    return this.dbService.removeRef(`/mealPlans/${userId}/${programId}`);
  }

  async getMealPlansAsPromise(userId): Promise<MealPlan[]> {
    const snap = await this.dbService.getSnapshot(`/mealPlans/${userId}`)
    const plans = this.helperService.transformFirebaseObjectToArray(snap);
    return plans.map((plan) => {
      return new MealPlan(plan)
    });
  }

  async getPublishedMealPlans(userId: string) {
    let plans = await this.getMealPlansAsPromise(userId);
    return plans.filter((plan: MealPlan) => {
      return plan.isPublished() || plan.createdBy === userId;
    }).sort((a: MealPlan, b: MealPlan) => {
      return +new Date(b.startDate) - +new Date(a.startDate);
    });;
  }

  async getActiveMealPlans(userId: string) {
    let plans = await this.getMealPlansAsPromise(userId);
    return plans.filter((plan: MealPlan) => {
      return plan.isPublished() || plan.createdBy === userId;
    }).sort((a: MealPlan, b: MealPlan) => {
      return +new Date(b.startDate) - +new Date(a.startDate);
    });;
  }


  getFoodIds(mealPlan: IMealPlanDetail): string[] {
    const foods = [];
    if(mealPlan.plans && mealPlan.plans.length > 0) {
      for (const plan of mealPlan.plans) {
        if(plan.meals && plan.meals.length > 0) {
          for (const meal of plan.meals) {
            if(meal.foods && meal.foods.length > 0) {
              for (const food of meal.foods) {
                if(!food.type || food.type === "food") {
                  const index = foods.indexOf(food.food);
                  if(index === -1) {
                    foods.push(food.food)
                  }
                }
              }
            }
          }
        }
      }
    }
    return foods;
  }

  getMealPlanRecipeIds(mp: IMealPlanDetail) {
    const ids = [];
    if(mp.plans?.length > 0) {
      for (const plan of mp.plans) {
        ids.push(...this.getPlanRecipeIds(plan))
      }
    }
    return ids;
  }

  getPlanRecipeIds(plan: Partial<IPlanDetail>): string[] {
    const recipes = [];
    if(plan.meals && plan.meals.length > 0) {
      for (const meal of plan.meals) {
        if(meal.foods && meal.foods.length > 0) {
          for (const food of meal.foods) {
            if(food.type === "recipe") {
              const index = recipes.indexOf(food.food);
              if(index === -1) {
                recipes.push(food.food)
              }
            }
          }
        }
      }
    }
    return recipes;
  }

  getPlanFoodIds(plan: Partial<IPlanDetail>): string[] {
    const foods = [];
    if(plan.meals && plan.meals.length > 0) {
      for (const meal of plan.meals) {
        if(meal.foods && meal.foods.length > 0) {
          for (const food of meal.foods) {
            if(!food.type || food.type === "food") {
              const index = foods.indexOf(food.food);
              if(index === -1) {
                foods.push(food.food)
              }
            }
          }
        }
      }
    }
    return foods;
  }

  getUserMealPlanList(userId: string) {
    return this.dbService.getListByRefTest<MealPlan>(`mealPlans/${userId}`);
  }

  updateUserMealPlan(userId:string, mealId: string, data: Partial<IMealPlanDetail>){
    return this.dbService.updateRef(`mealPlans/${userId}/${mealId}`, data);
  }

  updateUserMealPlanDraft(userId: string, mealPlanId: string, data: Partial<MealPlan>) {
    return this.dbService.updateRef(`/mealPlans/${userId}/${mealPlanId}/draftMealPlan`, data);
  }

  async saveUserMealPlan(orgId: string, userId:string , programId: string, mealPlanDetail: IMealPlanDetail, modifiedBy: string): Promise<ThenableReference> {
    let result;
    let mealPlan = {
      draftMealPlan: mealPlanDetail,
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      organization: orgId || null,
    };
    if(programId === "add") {
      result = await this.dbService.pushByRef(`/mealPlans/${userId}`, {
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        readNotes:false,
        ...mealPlan
      });
    } else {
      result = await this.dbService.updateRef(`/mealPlans/${userId}/${programId}`, mealPlan)
    }
    return result
  }

  async publishUserMealPlan(orgId: string, userId:string , programId: string, mealPlanDetail: IMealPlanDetail, modifiedBy: string, notification = true): Promise<ThenableReference> {
    let result;
    let mealPlan: Partial<MealPlan> = {
      ...mealPlanDetail,
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      draftMealPlan: null,
    }
    if(notification) {
      mealPlan.published = new Date().toISOString();
    }
    if(!programId || programId === "add") {
      result = await this.dbService.pushByRef(`/mealPlans/${userId}`, {
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        organization: orgId || null,
        ...mealPlan
      })
    } else {
      result = await this.dbService.updateRef(`/mealPlans/${userId}/${programId}`, mealPlan)
    }
    return result
  }

  /* Organization Meal Plans */
  getOrganizationMealPlanList(orgId: string) {
    return this.dbService.getListByRefTest(`organizationMealPlans/${orgId}`);
  }

  async getOrganizationMealPlansAsPromise(orgId: string): Promise<MealPlan[]> {
    const snap = await this.dbService.getSnapshot(`organizationMealPlans/${orgId}`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map(mp => {return new MealPlan(mp)}) : null;
  }

  async getOrganizationMealPlanAsPromise(organizationId: string, programId: string): Promise<MealPlan> {
    const snap = await this.dbService.getSnapshot(`organizationMealPlans/${organizationId}/${programId}`);
    return snap? new MealPlan({...snap, id: programId}) : null;
  }

  async saveOrganizationMealPlan(orgID: string, programId: string, planDetail: IMealPlanDetail, modifiedBy: string): Promise<ThenableReference> {
    let result;
    let protocol = {
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      organization: orgID,
      draftMealPlan: planDetail
    };
    if(programId === "add") {
      result = await this.dbService.pushByRef(`/organizationMealPlans/${orgID}`, {
        ...protocol,
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        organization: orgID,
      })
    } else {
      result = await this.dbService.updateRef(`/organizationMealPlans/${orgID}/${programId}`, protocol);

    }
    return result
  }

  async publishOrganizationMealPlan(orgID: string, programId: string, mealPlanDetail: IMealPlanDetail, modifiedBy: string): Promise<ThenableReference> {
    let result;
    let MP = {
      ...mealPlanDetail,
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      draftMealPlan: null,
      published: new Date().toISOString(),
    };
    if(!programId || programId === "add") {
      result = await this.dbService.pushByRef(`/organizationMealPlans/${orgID}`, {
        ...MP,
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        organization: orgID,
      })
    } else {
      result = await this.dbService.updateRef(`/organizationMealPlans/${orgID}/${programId}`, MP);
    }
    return result
  }

  deleteOrganizationMealPlan(orgId: string, mealplanId: string) {
    return this.dbService.removeRef(`organizationMealPlans/${orgId}/${mealplanId}`);
  }

  /* Organization Meals */
  async getMealsAsPromiseByOrganization(organizationId: string): Promise<IMeal[]> {
    const snap = await this.dbService.getSnapshot(`allMeals`,orderByChild("organization"), equalTo(organizationId));
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : null;
  }

  async getMealsAsPromiseByUser(userId: string): Promise<IMeal[]> {
    const snap = await this.dbService.getSnapshot(`allMeals`, orderByChild("createdBy"), equalTo(userId));
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : null;
  }

  async getMealAsPromise(mealId: string): Promise<IMeal> {
    const snap = await this.dbService.getSnapshot(`allMeals/${mealId}`)
    return snap? {...snap, uid: mealId } : null;
  }

  async addMeal(userId:string, data: IMeal) {
    return this.dbService.pushByRef(`allMeals`, {
      createdBy: userId,
      creationDate: new Date().toISOString(),       
      ...data
    });
  }

  updateMeal(mealId: string, data: Partial<Meal>) {
    return this.dbService.updateRef(`allMeals/${mealId}`, data);
  }

  deleteMeal(mealId: string) {
    return this.dbService.removeRef(`allMeals/${mealId}`);
  }

  /* Organization Menus */
  async getMenusAsPromiseByOrganization(organizationId: string): Promise<IMenuLibraryItem[]> {
    const snap = await this.dbService.getSnapshot(`organizationMenus/${organizationId}`);
    return snap ? this.helperService.transformFirebaseObjectToArray(snap) : null;
  }

  async getMenuAsPromise(organizationId: string, menuId: string): Promise<IMenuLibraryItem> {
    const snap = await this.dbService.getSnapshot(`organizationMenus/${organizationId}/${menuId}`);
    return snap ? { ...snap, uid: menuId } : null;
  }

  async addMenu(organizationId: string, userId: string, data: IMenuLibraryItem) {
    return this.dbService.pushByRef(`organizationMenus/${organizationId}`, {
      createdBy: userId,
      creationDate: new Date().toISOString(),
      organization: organizationId,
      ...data
    });
  }

  updateMenu(organizationId: string, menuId: string, data: Partial<IMenuLibraryItem>) {
    return this.dbService.updateRef(`organizationMenus/${organizationId}/${menuId}`, data);
  }

  deleteMenu(organizationId: string, menuId: string) {
    return this.dbService.removeRef(`organizationMenus/${organizationId}/${menuId}`);
  }

  async getTrackedPlan(userId: string, date: string): Promise<IPlanDetail> {
    const snap = await this.dbService.getSnapshot(`nutritionTracking/${userId}/${date}`);
    return snap? {id: date, ...snap} : null
  }

  async getTrackedPlans(userId: string, limit = 400): Promise<Array<IPlanDetail>> {
    const snap = await this.dbService.getSnapshot(`nutritionTracking/${userId}`, orderByChild("creationDate"), limitToLast(limit));
    return this.helperService.transformFirebaseObjectToArray(snap);
  }

  getTrackedPlanCommentsList(userId: string, planId: string) {
    return this.dbService.getListByRefTest(`/nutritionTracking/${userId}/${planId}/comments`);
  }

  addTrackedPlanComment(userId, trackingId, comment) {
    return this.dbService.pushByRef(`/nutritionTracking/${userId}/${trackingId}/comments`, comment);
  }


  deleteTrackedPlanComment(userId, trackingId, commentId) {
    return this.dbService.removeRef(`/nutritionTracking/${userId}/${trackingId}/comments/${commentId}`);
  }

  async addTrackedPlanCommentAttachment(
    userId,
    reportId,
    user: User,
    attachment: LocalAttachment,
    organization?: string
  ) {
    const storagePath = `users/${userId}/nutritionTracking/${reportId}/comments/${attachment.name}`;
    let attachmentRef = null;

    if(attachment.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file);
    }

    if(attachment.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }

    if(!attachmentRef) {
      throw new Error('No attachment reference found');
    }

    return this.addTrackedPlanComment(userId, reportId, {
      comment: null,
      creationDate: new Date().toISOString(),
      createdBy: user.uid,
      createdByName: user.getFullName(),
      organization: organization || null,
      attachments: [{
        url: attachmentRef,
        fileName: attachment.name,
        type: attachment.type,
      }]
    });
  }  

  getTrackedNutritionList(userId: string, limit = 400) {
    return this.dbService.getListByRefTest(`/nutritionTracking/${userId}`, orderByChild("creationDate"), limitToLast(limit));
  }

  async getTrackedNutritionByDate(userId: string, date: string): Promise<any> {
    const snap = await this.dbService.getSnapshot(`/nutritionTracking/${userId}`, orderByChild("date"), equalTo(date));
    return snap? this.helperService.transformFirebaseObjectToArray(snap)[0] : null;
  }

  /**
   * Prefer the RTDB key (calendar day path), then fall back to the `date` field.
   * Covers editDate key/field desync so blank navigation is not started against an occupied day.
   */
  async resolveTrackedNutritionForDate(userId: string, date: string): Promise<IPlanDetail | null> {
    if (!userId || !date) {
      return null;
    }
    const byKey = await this.getTrackedPlan(userId, date);
    if (byKey) {
      return byKey;
    }
    return (await this.getTrackedNutritionByDate(userId, date)) || null;
  }

  /**
   * 
   * @param userId
   * @param date 
   * @returns a string or null. This function is used to identify if a given day has a tracked meal plan
   */
  getTrackedPlanDate(userId: string, date: string): Promise<Array<IPlanDetail>> {
    return this.dbService.getSnapshot(`nutritionTracking/${userId}/${date}/date`);
  }

  /** True when meals contain any logged foods or supplements (empty slots alone do not count). */
  nutritionDayHasLoggedContent(plan: Partial<IPlanDetail> | null | undefined): boolean {
    if (!plan?.meals?.length) {
      return false;
    }
    return plan.meals.some((meal: any) =>
      (Array.isArray(meal?.foods) && meal.foods.length > 0) ||
      (Array.isArray(meal?.supplements) && meal.supplements.length > 0)
    );
  }

  private nutritionDayHasComments(plan: any): boolean {
    if (!plan?.comments) {
      return false;
    }
    if (Array.isArray(plan.comments)) {
      return plan.comments.length > 0;
    }
    return typeof plan.comments === 'object' && Object.keys(plan.comments).length > 0;
  }

  /**
   * Create (or replace) a tracked nutrition day via Firebase `set`.
   * Safety rail: never overwrite a non-empty day (foods, supplements, or comments)
   * with an empty blank payload. Explicit wipe remains `deleteTrackedPlan`.
   */
  async createTrackedPlan(
    userId: string,
    plan: IPlanDetail
  ): Promise<{ written: boolean; plan: IPlanDetail }> {
    const dateKey = plan?.date;
    if (!userId || !dateKey) {
      throw new Error('createTrackedPlan requires userId and plan.date');
    }

    const existing = await this.getTrackedPlan(userId, dateKey);
    const incomingEmpty = !this.nutritionDayHasLoggedContent(plan);
    const existingProtected =
      this.nutritionDayHasLoggedContent(existing) || this.nutritionDayHasComments(existing);

    if (existing && incomingEmpty && existingProtected) {
      return { written: false, plan: existing };
    }

    const toWrite: any = {
      ...plan,
      creationDate: (plan as any).creationDate || (existing as any)?.creationDate || new Date().toISOString(),
    };
    // Avoid persisting client-only id/uid on the node body.
    delete toWrite.id;
    delete toWrite.uid;

    await this.dbService.setRef(`nutritionTracking/${userId}/${dateKey}`, toWrite);
    return { written: true, plan: { id: dateKey, ...toWrite } };
  }

  updateTrackedPlan(userId: string, date: string, plan:  Partial<IPlanDetail>) {
    return this.dbService.updateRef(`nutritionTracking/${userId}/${date}`, plan);
  }

  deleteTrackedPlan(userId: string, date: string) {
    return this.dbService.removeRef(`nutritionTracking/${userId}/${date}`);
  }
}